import { logger } from '@/lib/logger';
import { useToastStore } from '@/stores/toastStore';
import { useQueryClient } from '@tanstack/vue-query';
import {
  featureToggleQueries,
  featureToggleKeys,
  featureToggleMutations,
} from '@therame/api-client';
import { FeatureToggleType, UpdateFeatureToggleDto } from '@therame/shared';
import { defineStore } from 'pinia';
import { ref, watch } from 'vue';

export const useFeatureToggleStore = defineStore('featureToggle', () => {
  const toastStore = useToastStore();
  const queryClient = useQueryClient();
  const _featureToggleId = ref<string>();
  const videoCallToggleId = ref<string>();
  const anamnesisToggleId = ref<string>();
  const shopToggleId = ref<string>();
  const patientAppointmentsToggleId = ref<string>();
  const patientBookingToggleId = ref<string>();

  const getFromLocalStorage = (key: string, defaultValue: boolean): boolean => {
    try {
      const stored = localStorage.getItem(key);
      return stored !== null ? JSON.parse(stored) : defaultValue;
    } catch {
      return defaultValue;
    }
  };

  const saveToLocalStorage = (key: string, value: boolean) => {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      logger.warn({ msg: 'Fehler beim Speichern in localStorage', error });
    }
  };

  const isVideoCallEnabled = ref<boolean>(getFromLocalStorage('featureToggle_videoCall', true));
  const isAnamnesisEnabled = ref<boolean>(getFromLocalStorage('featureToggle_anamnesis', true));
  const isShopEnabled = ref<boolean>(getFromLocalStorage('featureToggle_shop', true));
  // Patient self-service Terminübersicht (TM-9436). Defaults OFF, unlike the
  // three toggles above — handing patients a cancel button is a per-practice
  // decision, so nothing renders until an office opts in.
  const isPatientAppointmentsEnabled = ref<boolean>(
    getFromLocalStorage('featureToggle_patientAppointments', false),
  );
  // Patient self-booking (PRD #1343). Also defaults OFF and is gated separately
  // from the overview toggle above, so a practice can adopt booking stepwise.
  const isPatientBookingEnabled = ref<boolean>(
    getFromLocalStorage('featureToggle_patientBooking', false),
  );

  watch(isVideoCallEnabled, (newValue) => {
    saveToLocalStorage('featureToggle_videoCall', newValue);
  });

  watch(isAnamnesisEnabled, (newValue) => {
    saveToLocalStorage('featureToggle_anamnesis', newValue);
  });

  watch(isShopEnabled, (newValue) => {
    saveToLocalStorage('featureToggle_shop', newValue);
  });

  watch(isPatientAppointmentsEnabled, (newValue) => {
    saveToLocalStorage('featureToggle_patientAppointments', newValue);
  });

  watch(isPatientBookingEnabled, (newValue) => {
    saveToLocalStorage('featureToggle_patientBooking', newValue);
  });

  const loading = ref<boolean>(false);
  const saving = ref<boolean>(false);
  const currentOfficeLocationId = ref<string | null>(null);

  const loadFeatureToggles = async (officeLocationId: string) => {
    if (currentOfficeLocationId.value === officeLocationId && !loading.value) {
      return;
    }

    currentOfficeLocationId.value = officeLocationId;
    loading.value = true;

    try {
      const featureToggles = await queryClient.fetchQuery({
        queryKey: featureToggleKeys.byOfficeLocation(officeLocationId),
        queryFn: () => featureToggleQueries.getByOfficeLocation(officeLocationId),
      });

      const videoCallToggle = featureToggles.find(
        (featureToggle) => featureToggle.type === FeatureToggleType.VIDEO_CALL,
      );
      const anamnesisToggle = featureToggles.find(
        (featureToggle) => featureToggle.type === FeatureToggleType.ANAMNESIS,
      );
      const shopToggle = featureToggles.find(
        (featureToggle) => featureToggle.type === FeatureToggleType.SHOP,
      );
      const patientAppointmentsToggle = featureToggles.find(
        (featureToggle) => featureToggle.type === FeatureToggleType.PATIENT_APPOINTMENTS,
      );
      const patientBookingToggle = featureToggles.find(
        (featureToggle) => featureToggle.type === FeatureToggleType.PATIENT_BOOKING,
      );

      isVideoCallEnabled.value = videoCallToggle ? videoCallToggle.isEnabled : true;
      isAnamnesisEnabled.value = anamnesisToggle ? anamnesisToggle.isEnabled : true;
      isShopEnabled.value = shopToggle ? shopToggle.isEnabled : true;
      // Absence of a row means "off" for the patient-appointments toggle.
      isPatientAppointmentsEnabled.value = patientAppointmentsToggle
        ? patientAppointmentsToggle.isEnabled
        : false;
      // Same absence-is-off semantics for the booking toggle.
      isPatientBookingEnabled.value = patientBookingToggle ? patientBookingToggle.isEnabled : false;

      videoCallToggleId.value = videoCallToggle?.id;
      anamnesisToggleId.value = anamnesisToggle?.id;
      shopToggleId.value = shopToggle?.id;
      patientAppointmentsToggleId.value = patientAppointmentsToggle?.id;
      patientBookingToggleId.value = patientBookingToggle?.id;
    } catch (error) {
      logger.error({ msg: 'Fehler beim Laden der Feature Toggles', error });

      const fallbackValues = {
        videoCall: getFromLocalStorage('featureToggle_videoCall', true),
        anamnesis: getFromLocalStorage('featureToggle_anamnesis', true),
        shop: getFromLocalStorage('featureToggle_shop', true),
        patientAppointments: getFromLocalStorage('featureToggle_patientAppointments', false),
        patientBooking: getFromLocalStorage('featureToggle_patientBooking', false),
      };

      isVideoCallEnabled.value = fallbackValues.videoCall;
      isAnamnesisEnabled.value = fallbackValues.anamnesis;
      isShopEnabled.value = fallbackValues.shop;
      isPatientAppointmentsEnabled.value = fallbackValues.patientAppointments;
      isPatientBookingEnabled.value = fallbackValues.patientBooking;
    } finally {
      loading.value = false;
    }
  };

  const saveFeatureToggle = async (type: FeatureToggleType, newValue: boolean) => {
    if (!currentOfficeLocationId.value) {
      toastStore.addToast({
        text: 'Standort nicht geladen. Bitte versuche es erneut.',
        type: 'error',
      });
      return;
    }

    saving.value = true;
    try {
      const dto: UpdateFeatureToggleDto = new UpdateFeatureToggleDto();
      dto.type = type;
      dto.isEnabled = newValue;

      await featureToggleMutations.save(currentOfficeLocationId.value, dto);

      if (type === FeatureToggleType.VIDEO_CALL) {
        isVideoCallEnabled.value = newValue;
      } else if (type === FeatureToggleType.ANAMNESIS) {
        isAnamnesisEnabled.value = newValue;
      } else if (type === FeatureToggleType.SHOP) {
        isShopEnabled.value = newValue;
      } else if (type === FeatureToggleType.PATIENT_APPOINTMENTS) {
        isPatientAppointmentsEnabled.value = newValue;
      } else if (type === FeatureToggleType.PATIENT_BOOKING) {
        isPatientBookingEnabled.value = newValue;
      }

      toastStore.addToast({
        text: 'Einstellungen erfolgreich gespeichert.',
        type: 'success',
      });
    } catch {
      toastStore.addToast({
        text: 'Fehler beim Speichern der Einstellungen.',
        type: 'error',
      });
    } finally {
      saving.value = false;
    }
  };

  return {
    isVideoCallEnabled,
    isAnamnesisEnabled,
    isShopEnabled,
    isPatientAppointmentsEnabled,
    isPatientBookingEnabled,
    loading,
    saving,
    currentOfficeLocationId,
    loadFeatureToggles,
    saveFeatureToggle,
  };
});
